<template>
  <div class="animated fadeIn">
    <b-row>
      <b-col cols="12">
        <b-card>
          <div slot="header">
            <i class="fa fa-wrench"></i> Maintenance
          </div>

          <h5>Delete Metric Events</h5>
          <p class="text-muted">Permanently remove raw sensor readings from a device's metric table. This action cannot be undone.</p>

          <b-alert show variant="warning" class="mb-4">
            <i class="fa fa-exclamation-triangle mr-1"></i>
            <strong>Destructive operation.</strong> Deleted records cannot be recovered. Confirm the device and date range carefully before proceeding.
          </b-alert>

          <b-form>
            <b-row>
              <b-col md="6">
                <b-form-group label="Device" label-for="maint-device">
                  <div v-if="$apollo.queries.devices.loading">
                    <b-spinner small class="mr-1"></b-spinner> Loading devices...
                  </div>
                  <b-form-select
                    v-else
                    id="maint-device"
                    v-model="selectedDeviceId"
                    :options="deviceOptions"
                    @change="onDeviceChange"
                  >
                    <template #first>
                      <b-form-select-option :value="null">— Select a device —</b-form-select-option>
                    </template>
                  </b-form-select>
                  <small v-if="selectedDevice && !supportedType" class="text-danger">
                    <i class="fa fa-exclamation-circle mr-1"></i>Device type <strong>{{ selectedDevice.type }}</strong> has no metric table
                  </small>
                </b-form-group>
              </b-col>
            </b-row>

            <b-row>
              <b-col md="4">
                <b-form-group label="Start Date / Time" label-for="maint-start">
                  <b-form-input
                    id="maint-start"
                    v-model="form.startDate"
                    type="datetime-local"
                    :disabled="!selectedDevice || !supportedType"
                  ></b-form-input>
                  <small class="text-muted">Delete records <em>on or after</em> this time</small>
                </b-form-group>
              </b-col>
              <b-col md="4">
                <b-form-group label="End Date / Time" label-for="maint-end">
                  <b-form-input
                    id="maint-end"
                    v-model="form.endDate"
                    type="datetime-local"
                    :disabled="!selectedDevice || !supportedType"
                  ></b-form-input>
                  <small class="text-muted">Delete records <em>on or before</em> this time</small>
                </b-form-group>
              </b-col>
            </b-row>

            <!-- Preview of what will be deleted -->
            <b-row v-if="canSubmit">
              <b-col md="8">
                <div class="alert alert-info py-2 mb-3">
                  <strong>Scope:</strong>
                  {{ selectedDevice.name }} ({{ selectedDevice.serial }}) &mdash;
                  <span v-if="form.startDate">from {{ formatDisplay(form.startDate) }}</span>
                  <span v-else>beginning of time</span>
                  &nbsp;to&nbsp;
                  <span v-if="form.endDate">{{ formatDisplay(form.endDate) }}</span>
                  <span v-else>now</span>
                </div>
              </b-col>
            </b-row>

            <b-row>
              <b-col>
                <b-button
                  variant="danger"
                  :disabled="!canSubmit || deleting"
                  @click="showConfirm = true"
                >
                  <b-spinner v-if="deleting" small class="mr-1"></b-spinner>
                  <i v-else class="fa fa-trash mr-1"></i>
                  Delete Records
                </b-button>
              </b-col>
            </b-row>
          </b-form>

          <b-alert
            v-if="lastResult"
            :variant="lastResult.ok ? 'success' : 'danger'"
            show
            class="mt-3 mb-0"
          >
            <span v-if="lastResult.ok">
              <i class="fa fa-check mr-1"></i>
              Deleted <strong>{{ lastResult.count }}</strong> record<span v-if="lastResult.count !== 1">s</span>
              from the <strong>{{ lastResult.deviceType }}</strong> table.
            </span>
            <span v-else>
              <i class="fa fa-times mr-1"></i>
              {{ lastResult.message }}
            </span>
          </b-alert>
        </b-card>
      </b-col>
    </b-row>

    <!-- Confirmation modal -->
    <b-modal
      v-model="showConfirm"
      title="Confirm Deletion"
      ok-variant="danger"
      ok-title="Yes, Delete"
      cancel-title="Cancel"
      @ok="executeDelete"
    >
      <div v-if="selectedDevice">
        <p>You are about to permanently delete metric records for:</p>
        <table class="table table-sm table-bordered mb-3">
          <tbody>
            <tr>
              <th class="w-25">Device</th>
              <td>{{ selectedDevice.name }} <small class="text-muted">({{ selectedDevice.serial }})</small></td>
            </tr>
            <tr>
              <th>From</th>
              <td>{{ form.startDate ? formatDisplay(form.startDate) : 'Beginning of time' }}</td>
            </tr>
            <tr>
              <th>To</th>
              <td>{{ form.endDate ? formatDisplay(form.endDate) : 'Now' }}</td>
            </tr>
          </tbody>
        </table>
        <b-alert show variant="danger" class="mb-0 py-2">
          <i class="fa fa-exclamation-triangle mr-1"></i>
          <strong>This cannot be undone.</strong> Are you sure?
        </b-alert>
      </div>
    </b-modal>
  </div>
</template>

<script>
import { ALL_DEVICES_QUERY, DELETE_METRIC_EVENTS_MUTATION } from '@/constants/graphql'

const SUPPORTED_TYPES = ['well', 'flume', 'plunge', 'bubbler', 'river']

export default {
  name: 'Maintenance',
  apollo: {
    devices: {
      query: ALL_DEVICES_QUERY,
      update: function (data) { return data.devices || [] }
    }
  },
  data () {
    return {
      devices: [],
      selectedDeviceId: null,
      form: {
        startDate: '',
        endDate: ''
      },
      deleting: false,
      showConfirm: false,
      lastResult: null
    }
  },
  computed: {
    deviceOptions () {
      return (this.devices || []).map(function (d) {
        return {
          value: d.id,
          text: (d.name || d.serial) + ' (' + d.serial + ') — ' + d.type
        }
      })
    },
    selectedDevice () {
      if (!this.selectedDeviceId) return null
      var id = this.selectedDeviceId
      return (this.devices || []).find(function (d) { return d.id === id }) || null
    },
    supportedType () {
      if (!this.selectedDevice) return false
      return SUPPORTED_TYPES.indexOf(this.selectedDevice.type) !== -1
    },
    canSubmit () {
      if (!this.selectedDevice || !this.supportedType) return false
      return !!(this.form.startDate || this.form.endDate)
    }
  },
  methods: {
    onDeviceChange () {
      this.form.startDate = ''
      this.form.endDate = ''
      this.lastResult = null
    },

    formatDisplay (dateStr) {
      if (!dateStr) return ''
      return new Date(dateStr).toLocaleString()
    },

    async executeDelete () {
      this.deleting = true
      this.lastResult = null
      try {
        var input = { deviceId: this.selectedDevice.id }
        if (this.form.startDate) input.startDate = new Date(this.form.startDate).toISOString()
        if (this.form.endDate) input.endDate = new Date(this.form.endDate).toISOString()

        var result = await this.$apollo.mutate({
          mutation: DELETE_METRIC_EVENTS_MUTATION,
          variables: { input: input }
        })

        var data = result.data.deleteMetricEvents
        this.lastResult = { ok: true, count: data.count, deviceType: data.deviceType }
        this.form.startDate = ''
        this.form.endDate = ''
      } catch (e) {
        var msg = (e.graphQLErrors && e.graphQLErrors[0] && e.graphQLErrors[0].message) || e.message || 'Deletion failed'
        this.lastResult = { ok: false, message: msg }
      } finally {
        this.deleting = false
      }
    }
  }
}
</script>
